/**
 * Alert / Callout
 *
 * Layout: flex row, gap 10, padding 12 14, border 1 line,
 *         bg paper2, radius r3, font md, color ink2
 *
 * Tones (override bg / border / color):
 *   success → successSoft bg, successLine border, success color
 *   warn    → warnSoft bg, warnLine border, warn color
 *   danger  → dangerSoft bg, dangerLine border, danger color
 *   info    → infoSoft bg, infoLine border, info color
 *
 * Parts:
 *   icon     — auto-selected by tone if not provided
 *   title    — semibold, marginBottom 2 when children present
 *   children — body text
 *   dismiss  — x icon ghost button, opacity 0.7
 */

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ViewStyle,
  StyleProp,
  Platform,
} from 'react-native';
import { useTheme } from '../context/themeContext';

// ── Inline SVG icon paths ────────────────────────────────────
const ICON_PATHS: Record<string, string> = {
  check:         'M5 12l5 5L20 7',
  warn:          'M12 3 2 21h20L12 3zM12 10v4M12 18v.01',
  'alert-circle':'M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18zM12 8v.01M12 11v5',
  info:          'M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18zM12 8v.01M12 11v5',
  'check-circle':'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  key:           'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4',
  layers:        'M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  x:             'M6 6l12 12M18 6 6 18',
};

function AlertIcon({ name, color, size = 16 }: { name: string; color: string; size?: number }) {
  const d = ICON_PATHS[name];
  if (!d) return null;

  if (Platform.OS === 'web') {
    return (
      // @ts-ignore — web only
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{ flexShrink: 0, marginTop: 1 }}
      >
        <path d={d} />
      </svg>
    );
  }

  // React Native fallback glyphs
  const glyphs: Record<string, string> = {
    check:          '✓',
    warn:           '⚠',
    'alert-circle': '⚠',
    info:           'ℹ',
    'check-circle': '✓',
    key:            '🔑',
    layers:         '⊞',
    x:              '✕',
  };
  return (
    <Text style={{ fontSize: size - 2, color, lineHeight: size * 1.2 }}>
      {glyphs[name] ?? '•'}
    </Text>
  );
}

export type AlertTone = 'success' | 'warn' | 'danger' | 'info';

interface AlertProps {
  tone?: AlertTone;
  icon?: string;           // override auto icon
  title?: string;
  onDismiss?: () => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export function Alert({
  tone,
  icon,
  title,
  onDismiss,
  children,
  style,
}: AlertProps) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, spacing } = theme;

  // ── Tone → colors ────────────────────────────────────────
  type ToneColors = { bg: string; border: string; text: string };

  const toneMap: Record<AlertTone, ToneColors> = {
    success: { bg: colors.successSoft, border: colors.successLine, text: colors.success },
    warn:    { bg: colors.warnSoft,    border: colors.warnLine,    text: colors.warn    },
    danger:  { bg: colors.dangerSoft,  border: colors.dangerLine,  text: colors.danger  },
    info:    { bg: colors.infoSoft,    border: colors.infoLine,    text: colors.info    },
  };

  const defaultIcons: Record<AlertTone, string> = {
    success: 'check',
    warn:    'warn',
    danger:  'warn',
    info:    'info',
  };

  const tc: ToneColors = tone
    ? toneMap[tone]
    : { bg: colors.paper2, border: colors.line, text: colors.ink2 };

  const iconName = icon ?? (tone ? defaultIcons[tone] : undefined);

  return (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 10,
          paddingVertical: spacing[3],   // 12
          paddingHorizontal: 14,
          borderWidth: 1,
          borderColor: tc.border,
          backgroundColor: tc.bg,
          borderRadius: radii.r3,
        },
        style,
      ]}
      // @ts-ignore — web role
      role="alert"
    >
      {/* Icon */}
      {iconName ? (
        <AlertIcon name={iconName} color={tc.text} size={16} />
      ) : null}

      {/* Content */}
      <View style={{ flex: 1, minWidth: 0 }}>
        {title ? (
          <Text
            style={{
              fontSize: fontSize.md,
              fontWeight: fontWeight.semibold,
              color: tc.text,
              fontFamily: theme.font.sans,
              marginBottom: children ? 2 : 0,
            }}
          >
            {title}
          </Text>
        ) : null}

        {children ? (
          typeof children === 'string' ? (
            <Text
              style={{
                fontSize: fontSize.md,
                color: tc.text,
                fontFamily: theme.font.sans,
              }}
            >
              {children}
            </Text>
          ) : (
            children
          )
        ) : null}
      </View>

      {/* Dismiss */}
      {onDismiss ? (
        <TouchableOpacity
          onPress={onDismiss}
          accessibilityLabel="Dismiss"
          style={{
            opacity: 0.7,
            padding: 0,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
        >
          <AlertIcon name="x" color={tc.text} size={12} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

export default Alert;
